// ---------------------------------------------------------------------------
// How tall the lobby's action bar is.
//
// **This number was nowhere, and that was the bug.** The bar carrying Start
// game, Ready and the ready count is `sticky bottom-0` over the seat list, so
// the bottom of the screen is not the bottom of what a player can use. The host
// row menu measured itself against `window.innerHeight` minus the keyboard and
// nothing else, decided it had 247px of room under a row that actually had 142,
// and opened a menu with 101px of itself behind the bar. See the note on
// `place()` in `HostRowMenu.tsx` for the arithmetic that went wrong.
//
// **Measured, not declared, and that is the whole point of the file.** A
// constant would have to be one number, and the bar does not have one: it is a
// wrapping flex row, so on a 375px phone with a host seat in it the bar comes
// out at 105px against about 68 on a wider one. Any constant would have been
// wrong on exactly the screen the bug was reported on. So the bar reports its
// own height and everything else reads it here.
//
// It is published two ways from one measurement, because the two consumers ask
// in different languages:
//
//   - `useActionBarHeight()` for the arithmetic in `place()`.
//   - `--action-bar-height` on the document element, so a stylesheet or an
//     inline `calc()` can hold something clear of the bar without a round trip
//     through React.
//
// A module singleton rather than context, for the same reason `useVisualViewport`
// is one: there is one lobby on screen, the readers are scattered down the tree,
// and a provider would be a wrapper whose only job is to carry one number. The
// snapshot is a plain number, so `useSyncExternalStore`'s identity comparison is
// value comparison and the infinite-render trap that hook's file warns about
// cannot be sprung here.
// ---------------------------------------------------------------------------

import { useSyncExternalStore } from 'react';

let height = 0;
const listeners = new Set<() => void>();
let observer: ResizeObserver | null = null;

function publish(next: number): void {
  const rounded = Math.round(next);
  if (rounded === height) return;
  height = rounded;
  if (typeof document !== 'undefined') {
    document.documentElement.style.setProperty('--action-bar-height', `${rounded}px`);
  }
  for (const listener of listeners) listener();
}

/**
 * The ref the bar hands its own element to.
 *
 * Written as the classic null-on-unmount ref callback rather than one returning
 * a cleanup: the same shape works in every React this has been built against,
 * and there is exactly one teardown to do either way.
 *
 * The unmount publishes zero deliberately. The lobby is the only screen with a
 * bar, and a menu opened on the reveal or the end screen has the whole window.
 */
export function actionBarRef(node: HTMLElement | null): void {
  observer?.disconnect();
  observer = null;
  if (node === null) {
    publish(0);
    return;
  }
  publish(node.getBoundingClientRect().height);
  // Absent in jsdom, and in a test the initial measurement is the whole of it.
  if (typeof ResizeObserver === 'undefined') return;
  // The bar wraps, so its height changes with the window and with what is in it
  // — a Ready button appears when you take a seat, the "not recorded" chip
  // appears when host rulings go on. Both are ordinary lobby events and both
  // move the ceiling every open menu is measured against.
  observer = new ResizeObserver(() => publish(node.getBoundingClientRect().height));
  observer.observe(node);
}

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => listeners.delete(onChange);
}

/**
 * How much of the bottom of the layout viewport the action bar is sitting on.
 *
 * `subscribe` is a module function, so it is already stable across renders and
 * needs no `useCallback` around it.
 */
export function useActionBarHeight(): number {
  return useSyncExternalStore(
    subscribe,
    () => height,
    () => 0,
  );
}
